import { useCallback, useMemo } from 'react';

import { PLAYER_PLACEHOLDER_IMAGE_URL } from '@bo-constants/newWagers';
import type { PollData } from '@library/types/polls';
import { PollsKebabMenu } from '@bo-components/polls/PollsKebabMenu';
import { formatDate } from '@library/component-utils/dateTime';
import { isTimeAfterEST } from '@library/utils/dateTime';

export interface PollsTableRowProps {
  poll: PollData;
  handleViewPollClick: (poll: PollData) => void;
  handleEditWinnerClick: (poll: PollData) => void;
}

export const PollsTableRow = ({
  poll,
  handleViewPollClick,
  handleEditWinnerClick
}: PollsTableRowProps) => {
  const {
    id: pollId,
    imageUrl,
    description,
    question,
    options,
    startAt,
    endAt,
    winner
  } = poll;

  const pollHasEnded = useMemo(() => {
    return isTimeAfterEST(endAt);
  }, [endAt]);

  const onViewPollClick = useCallback(() => {
    handleViewPollClick(poll);
  }, [handleViewPollClick, poll]);

  const onEditWinnerClick = useCallback(() => {
    handleEditWinnerClick(poll);
  }, [handleEditWinnerClick, poll]);

  const pollMenuActions = {
    onViewPoll: () => onViewPollClick(),
    ...(!winner && pollHasEnded && { onEditWinner: () => onEditWinnerClick() })
  };

  const winnerText = useMemo(() => {
    return winner
      ? options.find((option) => option.id === winner)?.answer
      : 'Not settled';
  }, [winner, options]);

  return (
    <div className="border-soFresh-140 hover:bg-soFresh-120 text-soClean-110 flex min-h-16 w-full items-center justify-start border-b text-start xl:text-xs">
      <p
        className="line-clamp-2 w-[5%] max-w-[5%] overflow-hidden text-ellipsis px-3"
        data-testid={`poll-${pollId}`}
      >
        {pollId}
      </p>
      <p
        className="flex w-[5%] max-w-[5%] justify-start px-3"
        data-testid="poll-image"
      >
        <img
          src={imageUrl || PLAYER_PLACEHOLDER_IMAGE_URL}
          alt="Player"
          className="bg-soFresh-120 size-auto max-w-full rounded-md placeholder:h-12 placeholder:w-auto"
        />
      </p>
      <p
        className="line-clamp-2 w-1/5 max-w-[20%] overflow-hidden text-ellipsis px-3"
        data-testid="poll-question"
      >
        {question}
      </p>
      <p
        className="line-clamp-2 w-1/5 max-w-[20%] overflow-hidden text-ellipsis px-3"
        data-testid="poll-description"
      >
        {description}
      </p>
      <div className="w-[15%] max-w-[15%] p-3" data-testid="options">
        {options?.length
          ? options.map((option) => {
              return (
                <p key={option.id} style={{ display: 'flex' }}>
                  <span>{option.answer}</span>&nbsp;
                  <span className="text-spottieOttie">
                    (&nbsp;{option.votes_count}&nbsp;)
                  </span>
                </p>
              );
            })
          : null}
      </div>
      <p className="w-[10%] max-w-[10%] px-3" data-testid="options">
        {winnerText}
      </p>
      <p className="w-[10%] max-w-[10%] px-3" data-testid="poll-start-at">
        {formatDate({ date: startAt })}
      </p>
      <p className="w-[10%] max-w-[10%] px-3" data-testid="poll-end-at">
        {formatDate({ date: endAt })}
      </p>

      <div className="w-[5%] max-w-[5%] px-3" data-testid="poll-actions">
        <PollsKebabMenu menuActions={pollMenuActions} id={pollId} />
      </div>
    </div>
  );
};
